import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './MondaHomePage.css';

import assistantImage from '../../assets/images/mondaHome/assistant.webp';
import cultureImage from '../../assets/images/mondaHome/culture.webp';
import familyImage from '../../assets/images/mondaHome/family.webp';
import finalParallaxImage from '../../assets/images/mondaHome/final-parallax.webp';
import gamePathImage from '../../assets/images/mondaHome/game-path.webp';
import heroBoyImage from '../../assets/images/mondaHome/hero-boy.webp';
import heroGirlImage from '../../assets/images/mondaHome/hero-girl.webp';
import personalPathImage from '../../assets/images/mondaHome/personal-path.webp';
import progressImage from '../../assets/images/mondaHome/progress.webp';

const slides = [
  {
    eyebrow: 'ابزار طراحی محتوای آموزشی برای کلاس و خانه',
    title: (
      <>
        آموزش را دیدنی کنید؛ <strong>یادگیری را ماندگار</strong>
      </>
    ),
    description:
      'مندا به معلمان و فعالان آموزشی کمک می‌کند درس، تمرین و فعالیت کلاسی را با قالب‌ها و ابزارهای تصویری ساده، حرفه‌ای و متناسب با پایه تحصیلی طراحی کنند.',
    primaryText: 'مشاهده امکانات طراحی',
    primaryHref: '#features',
    secondaryText: 'مندا برای چه کسانی است؟',
    secondaryHref: '#families',
    proof: ['قالب‌های دسته‌بندی‌شده بر اساس پایه', 'خروجی آماده استفاده در آموزش'],
    image: heroBoyImage,
    imageAlt: 'کودک در مسیر یادگیری بازی‌محور مندا',
    topCardIcon: '🎯',
    topCardTitle: 'یک درس، یک تجربه تازه',
    topCardText: 'قابل ویرایش برای کلاس شما',
    bottomCardIcon: '★',
    bottomCardTitle: 'محتوای آماده کلاس',
    bottomCardText: 'ساده، تصویری و هدفمند',
  },
  {
    eyebrow: 'از هدف آموزشی تا فعالیت تصویری',
    title: (
      <>
        مفهوم‌های دشوار را <strong>ساده، تصویری و قابل‌تمرین کنید</strong>
      </>
    ),
    description:
      'از آموزش ساعت و جمع ریاضی تا شناخت حیوانات و مفاهیم علوم؛ تصویر، متن و عناصر آموزشی را کنار هم بگذارید و محتوایی بسازید که دانش‌آموز بتواند آن را ببیند، درک کند و انجام دهد.',
    primaryText: 'دیدن فرایند ساخت محتوا',
    primaryHref: '#smart-path',
    secondaryText: 'کاربرد مندا در آموزش',
    secondaryHref: '#benefits',
    proof: ['مناسب تدریس حضوری و آنلاین', 'قابل‌فهم برای کودک و خانواده'],
    image: heroGirlImage,
    imageAlt: 'دانش‌آموز در حال انجام مأموریت آموزشی مندا',
    topCardIcon: '🏅',
    topCardTitle: 'یادگیری با مشارکت',
    topCardText: 'دیدن، ساختن و تمرین‌کردن',
    bottomCardIcon: '↗',
    bottomCardTitle: 'از ایده تا اجرا',
    bottomCardText: 'در یک محیط طراحی ساده',
  },
];

const MondaButton = ({ href, children, primary = false }) => {
  const className = `monda-home-button${primary ? ' monda-home-button-primary' : ''}`;

  if (href.charAt(0) === '#') {
    return (
      <a className={className} href={href}>
        {children}
      </a>
    );
  }

  return (
    <Link className={className} to={href}>
      {children}
    </Link>
  );
};

MondaButton.propTypes = {
  children: PropTypes.node.isRequired,
  href: PropTypes.string.isRequired,
  primary: PropTypes.bool,
};

const CheckList = ({ items }) => (
  <div className="monda-home-list">
    {items.map(item => (
      <div key={item}>
        <b aria-hidden="true">✓</b>
        <span>{item}</span>
      </div>
    ))}
  </div>
);

CheckList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const MondaHomePage = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [sliderPaused, setSliderPaused] = useState(false);
  const finalSectionRef = useRef(null);

  useEffect(() => {
    if (sliderPaused) return undefined;

    const timer = window.setInterval(() => {
      setActiveSlide(currentSlide => (currentSlide + 1) % slides.length);
    }, 8000);

    return () => window.clearInterval(timer);
  }, [sliderPaused]);

  useEffect(() => {
    const section = finalSectionRef.current;
    if (!section) return undefined;

    let animationFrame = null;

    const updateParallax = () => {
      animationFrame = null;
      const sectionRect = section.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      if (sectionRect.bottom < 0 || sectionRect.top > viewportHeight) return;

      const sectionCenter = sectionRect.top + sectionRect.height / 2;
      const viewportCenter = viewportHeight / 2;
      const maximumOffset = window.innerWidth <= 600 ? 40 : 68;
      const movementRatio = window.innerWidth <= 600 ? 0.12 : 0.16;
      const parallaxOffset = Math.max(
        -maximumOffset,
        Math.min(maximumOffset, (viewportCenter - sectionCenter) * movementRatio)
      );

      section.style.setProperty('--monda-home-parallax-y', `${parallaxOffset}px`);
    };

    const requestParallaxUpdate = () => {
      if (animationFrame === null) {
        animationFrame = window.requestAnimationFrame(updateParallax);
      }
    };

    updateParallax();
    // This layout scrolls on body instead of window. Capturing at document level
    // also keeps the effect working if the page's scroll container changes later.
    document.addEventListener('scroll', requestParallaxUpdate, {
      capture: true,
      passive: true,
    });
    window.addEventListener('resize', requestParallaxUpdate);

    return () => {
      document.removeEventListener('scroll', requestParallaxUpdate, true);
      window.removeEventListener('resize', requestParallaxUpdate);
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  const showPreviousSlide = () => {
    setActiveSlide(currentSlide => (currentSlide - 1 + slides.length) % slides.length);
  };

  const showNextSlide = () => {
    setActiveSlide(currentSlide => (currentSlide + 1) % slides.length);
  };

  return (
    <div className="monda-home-page" dir="rtl">
      <section
        className="monda-home-hero"
        aria-label="معرفی مندا"
        onMouseEnter={() => setSliderPaused(true)}
        onMouseLeave={() => setSliderPaused(false)}
        onFocus={() => setSliderPaused(true)}
        onBlur={() => setSliderPaused(false)}
      >
        {slides.map((slide, index) => (
          <article
            className={`monda-home-slide monda-home-shell${activeSlide === index ? ' is-active' : ''}`}
            key={slide.eyebrow}
            aria-hidden={activeSlide !== index}
          >
            <div className="monda-home-hero-copy">
              <span className="monda-home-eyebrow">{slide.eyebrow}</span>
              <h1>{slide.title}</h1>
              <p className="monda-home-lead">{slide.description}</p>
              <div className="monda-home-actions">
                <MondaButton href={slide.primaryHref} primary>
                  {slide.primaryText}
                </MondaButton>
                <MondaButton href={slide.secondaryHref}>{slide.secondaryText}</MondaButton>
              </div>
              <div className="monda-home-proof">
                {slide.proof.map(item => (
                  <span key={item}>
                    <i aria-hidden="true">✓</i>
                    {item}
                  </span>
                ))}
              </div>
            </div>
            <div className="monda-home-hero-art">
              <img src={slide.image} alt={slide.imageAlt} />
              <div className="monda-home-floating monda-home-floating-top">
                <span aria-hidden="true">{slide.topCardIcon}</span>
                <div>
                  <strong>{slide.topCardTitle}</strong>
                  <small>{slide.topCardText}</small>
                </div>
              </div>
              <div className="monda-home-floating monda-home-floating-bottom">
                <span aria-hidden="true">{slide.bottomCardIcon}</span>
                <div>
                  <strong>{slide.bottomCardTitle}</strong>
                  <small>{slide.bottomCardText}</small>
                </div>
              </div>
            </div>
          </article>
        ))}

        <div className="monda-home-slider-controls" aria-label="کنترل اسلایدر">
          <button
            type="button"
            className="monda-home-arrow"
            onClick={showPreviousSlide}
            aria-label="اسلاید قبلی"
            dir="ltr"
          >
            ‹
          </button>
          {slides.map((slide, index) => (
            <button
              type="button"
              className={`monda-home-dot${activeSlide === index ? ' is-active' : ''}`}
              key={slide.eyebrow}
              onClick={() => setActiveSlide(index)}
              aria-label={`نمایش اسلاید ${index + 1}`}
              aria-pressed={activeSlide === index}
            />
          ))}
          <button
            type="button"
            className="monda-home-arrow"
            onClick={showNextSlide}
            aria-label="اسلاید بعدی"
            dir="ltr"
          >
            ›
          </button>
        </div>
      </section>

      <div className="monda-home-trust" aria-label="ارزش‌های اصلی مندا">
        <div>
          <strong>برای دانش‌آموز قابل‌فهم</strong>
          <span>یادگیری با دیدن، ساختن و انجام‌دادن</span>
        </div>
        <div>
          <strong>برای معلم کاربردی</strong>
          <span>ساخت سریع محتوای متناسب با هدف درس</span>
        </div>
        <div>
          <strong>برای والدین روشن</strong>
          <span>درک ساده‌تر موضوع و هدف هر فعالیت</span>
        </div>
      </div>

      <section className="monda-home-intro monda-home-shell" id="aboutVibino">
        <span className="monda-home-kicker">مندا چیست؟</span>
        <h2>محیطی برای تبدیل هدف‌های آموزشی به محتوای تصویری قابل‌استفاده</h2>
        <p>
          مندا یک ابزار طراحی آموزشی آنلاین است که به معلم، مربی و تولیدکننده محتوا امکان می‌دهد
          برای هر پایه و موضوع درسی، فعالیت‌های دیداری، تمرین‌های کلاسی و محتوای قابل‌ویرایش بسازد؛
          محتوایی که برای دانش‌آموز جذاب و برای والدین نیز قابل‌فهم باشد.
        </p>
        <div className="monda-home-stats">
          <div>
            <strong className="monda-home-stat-number" dir="ltr">+۴۷</strong>
            <span>موضوع و مسیر آموزشی</span>
          </div>
          <div>
            <strong className="monda-home-stat-number" dir="ltr">+۱٬۲۵۰</strong>
            <span>فعالیت آموزشی طراحی‌شده</span>
          </div>
          <div>
            <strong className="monda-home-stat-number">۳ همراه</strong>
            <span>دانش‌آموز، خانواده و معلم در یک مسیر</span>
          </div>
        </div>
      </section>

      <section className="monda-home-section monda-home-section-soft" id="features">
        <div className="monda-home-grid monda-home-shell">
          <div className="monda-home-media monda-home-media-yellow">
            <img src={gamePathImage} alt="مسیر مرحله‌ای و بازی‌محور یادگیری" />
          </div>
          <div>
            <span className="monda-home-kicker">امکانات مندا برای آموزش</span>
            <h2>از یک هدف درسی تا محتوای آماده کلاس، در یک محیط ساده</h2>
            <p>
              قالب مناسب پایه را انتخاب کنید، متن و تصویر را تغییر دهید و با عناصر آموزشی، تمرین دلخواه
              خود را بسازید. نتیجه را می‌توانید در کلاس، آموزش آنلاین، تکلیف یا فعالیت خانگی استفاده کنید.
            </p>
            <CheckList
              items={[
                'قالب‌های آموزشی دسته‌بندی‌شده بر اساس پایه تحصیلی',
                'ویرایش ساده متن، تصویر، شکل و عناصر آموزشی',
                'دریافت خروجی مناسب نمایش، چاپ و اشتراک‌گذاری',
              ]}
            />
          </div>
        </div>
      </section>

      <section className="monda-home-section" id="benefits">
        <div className="monda-home-grid monda-home-grid-reverse monda-home-shell">
          <div className="monda-home-media">
            <img src={progressImage} alt="رشد مهارت‌های کودک در مندا" />
          </div>
          <div>
            <span className="monda-home-kicker">مزایای آموزش دیداری و فعال</span>
            <h2>دانش‌آموز فقط دریافت‌کننده نیست؛ در فرایند یادگیری مشارکت می‌کند</h2>
            <p>
              ترکیب توضیح کوتاه، تصویر و فعالیت عملی کمک می‌کند مفهوم درس ملموس‌تر شود. دانش‌آموز
              ضمن تمرین موضوع درسی، فرصت پیدا می‌کند تمرکز، خلاقیت، حل مسئله و انعطاف‌پذیری را نیز به کار بگیرد.
            </p>
            <div className="monda-home-skills">
              <div><i aria-hidden="true">◎</i><span>تمرکز</span></div>
              <div><i aria-hidden="true">✦</i><span>خلاقیت</span></div>
              <div><i aria-hidden="true">◇</i><span>حل مسئله</span></div>
              <div><i aria-hidden="true">♡</i><span>انعطاف‌پذیری</span></div>
            </div>
          </div>
        </div>
      </section>

      <section className="monda-home-section monda-home-section-soft" id="culture">
        <div className="monda-home-grid monda-home-shell">
          <div className="monda-home-media monda-home-media-yellow">
            <img src={cultureImage} alt="یادگیری با داستان‌های ایرانی" />
          </div>
          <div>
            <span className="monda-home-kicker">متناسب با کلاس و کودک ایرانی</span>
            <h2>محتوایی نزدیک به برنامه درسی، زبان و تجربه روزمره دانش‌آموز</h2>
            <p>
              معلم می‌تواند قالب و عناصر هر فعالیت را با موضوع درس، سطح کلاس و فضای فرهنگی دانش‌آموزان
              هماهنگ کند؛ از مثال‌های آشنا استفاده کند و محتوایی بسازد که ارتباط با آن آسان‌تر باشد.
            </p>
            <CheckList
              items={[
                'قابل تنظیم برای پایه و موضوع درسی',
                'امکان استفاده از تصویرها و مثال‌های آشنا',
                'مناسب فعالیت فردی، گروهی و گفت‌وگوی خانوادگی',
              ]}
            />
          </div>
        </div>
      </section>

      <div
        className="monda-home-final"
        ref={finalSectionRef}
        style={{ '--monda-home-final-image': `url(${finalParallaxImage})` }}
      >
        <h2>برای درس بعدی، یک تجربه تصویری روشن و به‌یادماندنی بسازید</h2>
        <p>با مندا، ایده آموزشی شما به محتوایی تبدیل می‌شود که معلم به‌سادگی ارائه می‌کند و دانش‌آموز با علاقه در آن مشارکت دارد.</p>
        <div className="monda-home-actions">
          <MondaButton href="#register" primary>شروع با مندا</MondaButton>
          <MondaButton href="/support">درخواست معرفی برای مدرسه</MondaButton>
        </div>
      </div>

      <section className="monda-home-section" id="smart-path">
        <div className="monda-home-grid monda-home-grid-reverse monda-home-shell">
          <div className="monda-home-media">
            <img src={personalPathImage} alt="مسیر یادگیری شخصی کودک" />
          </div>
          <div>
            <span className="monda-home-kicker">ساختار آموزشی منظم و قابل توسعه</span>
            <h2>پایه، موضوع و هدف درس را مشخص کنید؛ سپس محتوا را برای کلاس خود بسازید</h2>
            <p>
              قالب‌ها بر اساس پایه تحصیلی سامان‌دهی می‌شوند و هر فعالیت می‌تواند با تصویر، متن و
              عناصر مرتبط با درس شخصی‌سازی شود. ویدیوی کوتاه همراه قالب نیز می‌تواند روش تکمیل فعالیت را توضیح دهد.
            </p>
            <CheckList
              items={[
                'دسترسی سریع به محتوای مرتبط با پایه تحصیلی',
                'شخصی‌سازی فعالیت متناسب با نیاز کلاس',
                'راهنمای کوتاه برای استفاده درست از هر قالب',
              ]}
            />
          </div>
        </div>
      </section>

      <section className="monda-home-section monda-home-section-soft" id="assistant">
        <div className="monda-home-grid monda-home-shell">
          <div className="monda-home-media monda-home-media-yellow">
            <img src={assistantImage} alt="ابزار طراحی آموزشی مندا برای معلم و دانش‌آموز" />
          </div>
          <div>
            <span className="monda-home-kicker">طراحی ساده، بدون نیاز به تجربه گرافیکی</span>
            <h2>تمرکز شما روی آموزش می‌ماند؛ ابزارهای مندا ساخت محتوا را ساده می‌کنند</h2>
            <p>
              برای شروع لازم نیست طراح حرفه‌ای باشید. یک قالب آماده انتخاب کنید، اجزای آن را متناسب با درس
              تغییر دهید و مرحله‌به‌مرحله به محتوایی برسید که با شیوه تدریس شما هماهنگ است.
            </p>
            <CheckList
              items={[
                'شروع سریع با قالب‌های از پیش طراحی‌شده',
                'کنترل کامل معلم بر متن، تصویر و چیدمان',
                'امکان بازبینی و استفاده دوباره از محتوای ساخته‌شده',
              ]}
            />
          </div>
        </div>
      </section>

      <section className="monda-home-families" id="families">
        <div className="monda-home-family-story monda-home-shell">
          <div className="monda-home-media">
            <img src={familyImage} alt="خانواده در حال همراهی کودک در مسیر یادگیری" />
          </div>
          <div>
            <span className="monda-home-kicker">پیوند بهتر میان آموزش در کلاس و خانه</span>
            <h2>وقتی هدف فعالیت روشن باشد، همراهی با دانش‌آموز آسان‌تر می‌شود</h2>
            <p>
              محتوای تصویری به معلم کمک می‌کند انتظار آموزشی را شفاف‌تر توضیح دهد، به دانش‌آموز امکان
              تمرین فعال می‌دهد و والدین را با موضوع و هدف فعالیت همراه می‌کند.
            </p>
          </div>
        </div>

        <div className="monda-home-roles monda-home-shell">
          <span className="monda-home-kicker">یک ابزار، برای سه نقش مهم</span>
          <h2>مندا نیاز معلم، دانش‌آموز و خانواده را کنار هم می‌بیند</h2>
          <p>
            معلم محتوای هدفمند می‌سازد، دانش‌آموز با تصویر و فعالیت بهتر درگیر می‌شود و خانواده با زبان
            ساده‌تری موضوع درس و روش همراهی را درک می‌کند.
          </p>
          <div className="monda-home-role-grid">
            <article>
              <img src={heroGirlImage} alt="دانش‌آموز" />
              <div><h3>برای دانش‌آموزان</h3><p>مفهوم‌ها را با تصویر، مثال و فعالیت می‌بینند و آموخته‌های خود را به کار می‌گیرند.</p></div>
            </article>
            <article>
              <img src={assistantImage} alt="معلم" />
              <div><h3>برای معلمان و مربیان</h3><p>محتوای آموزشی متناسب با پایه و هدف درس را سریع‌تر طراحی، ویرایش و آماده ارائه می‌کنند.</p></div>
            </article>
            <article>
              <img src={familyImage} alt="والدین" />
              <div><h3>برای والدین</h3><p>موضوع تمرین و هدف آموزشی آن را روشن‌تر می‌فهمند و در خانه همراهی مؤثرتری دارند.</p></div>
            </article>
          </div>
        </div>

      </section>

      <footer className="monda-home-footer monda-home-shell">
        <div className="monda-home-footer-brand"><span>م</span><strong>مندا</strong></div>
        <p>طراحی آموزشی برای یادگیری روشن‌تر و فعال‌تر</p>
      </footer>
    </div>
  );
};

export default MondaHomePage;
